/**
 * Journey URLs carry the brand as their leading path segment, so the brand survives anything the
 * customer can do to a URL — a refresh, a copied link in a second tab, a bookmark, or being
 * bounced to the landing page and returned via ?url=.
 *
 * CD-8652 persisted the brand in sessionStorage, which cannot survive a new tab. Rather than
 * widening that storage, the brand lives in the URL where it is self-describing and cannot go
 * stale. See CD-8653 AC5.
 */

import { ReferralBrand, brandFromPathname } from './brand';
import { resolveReferralBrand }             from './resolveBrand';

/**
 * Route pattern shared by every post-lookup page. The brand segment is optional and constrained
 * to the two known values, so:
 *   - /energy/referral/123/evidence          resolves its brand from the URL
 *   - /referral/123/evidence                still resolves (brand falls back to storage), which
 *                                           keeps links already in flight working
 *   - /water/referral/123/evidence           does not match, so no URL-supplied value can ever
 *                                           reach branding, links or copy
 */
export const JOURNEY_ROUTE = '/:brand(energy|communications)?/referral/:complaintId';

/** Matches both the branded and the legacy unbranded shape, landing page included. */
const REFERRAL_PATH = /^\/(?:energy\/|communications\/)?referral(?:\/|$)/;

export const isReferralPath = (pathname: string): boolean => REFERRAL_PATH.test(pathname);

/** A brand's case-lookup landing page. */
export const landingPath = (brand: ReferralBrand): string => `/${brand}/referral`;

/** A page inside the journey. Omit `page` for the case-details page itself. */
export function journeyPath(brand: ReferralBrand, complaintId: string | number, page?: string): string {
  const base = `/${brand}/referral/${complaintId}`;
  return page ? `${base}/${page}` : base;
}

/**
 * Where PrivateRoute sends a customer who reaches a journey page without a session — their own
 * brand's landing page, never the wrong Ombudsman's. Returns null for non-referral paths so the
 * generic sign-in behaviour is untouched.
 *
 * The brand comes from the URL when it is there, and from storage for legacy unbranded links.
 */
export function referralBounceTarget(pathname: string): string | null {
  if (!isReferralPath(pathname)) return null;
  return landingPath(brandFromPathname(pathname) ?? resolveReferralBrand(pathname));
}
